import React, { createContext, useContext, useState, useEffect } from 'react';
import translateText from '../utils/translate.js';

// Create context
const AtozasLanguageContext = createContext();

/**
 * LanguageProvider component that manages language state
 * Stores selected language in localStorage and restores on reload
 * This is our own implementation to avoid JSX transpilation issues with the package
 */
export function AtozasLanguageProvider({ children }) {
  // Get initial language from localStorage or default to "en"
  const getInitialLanguage = () => {
    try {
      const stored = localStorage.getItem('atozas-traslate-language');
      return stored || 'en';
    } catch (error) {
      // localStorage might not be available (SSR, private browsing)
      return 'en';
    }
  };

  const [language, setLanguageState] = useState(getInitialLanguage);

  // Update localStorage when language changes
  useEffect(() => {
    try {
      localStorage.setItem('atozas-traslate-language', language);
    } catch (error) {
      // localStorage might not be available
      console.warn('Could not save language to localStorage:', error);
    }
  }, [language]);

  // Function to update language
  const setLanguage = (newLanguage) => {
    if (typeof newLanguage === 'string' && newLanguage.trim() !== '') {
      setLanguageState(newLanguage.trim());
    }
  };

  return (
    <AtozasLanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </AtozasLanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 * @returns {Object} Object with language and setLanguage
 */
export function useAtozasLanguage() {
  const context = useContext(AtozasLanguageContext);

  if (!context) {
    throw new Error('useAtozasLanguage must be used within an AtozasLanguageProvider');
  }

  return context;
}

/**
 * Custom hook for translating text based on current language
 * @param {string} text - Text to translate
 * @param {string} sourceLanguage - Source language code (default: "auto")
 * @returns {string} Translated text or original text if language is "en"
 */
export function useAtozasTranslate(text, sourceLanguage = 'auto') {
  const { language } = useAtozasLanguage();
  const [translatedText, setTranslatedText] = useState(text || '');

  useEffect(() => {
    // Handle empty/null text safely
    if (!text || typeof text !== 'string' || text.trim() === '') {
      setTranslatedText(text || '');
      return;
    }

    // If language is "en", return original text (no translation needed)
    if (language === 'en') {
      setTranslatedText(text);
      return;
    }

    // Translate text
    translateText(text, sourceLanguage, language)
      .then((translated) => {
        setTranslatedText(translated);
      })
      .catch((error) => {
        // On error, use original text
        console.error('Translation error in useAtozasTranslate:', error);
        setTranslatedText(text);
      });
  }, [text, language, sourceLanguage]);

  return translatedText;
}

